import { type UserRight, isSettingsOrganizationOn } from '@rentguru/commons-utils';
import { isNil } from 'lodash';

import { IMPERSONATION_BLOCKED_CODE, IMPERSONATION_BLOCKED_MESSAGE_ID } from 'src/utils/impersonationUtils';

const GENERIC_ERROR_ID = 'login.phoneVerification.errors.generic';
export const PHONE_NUMBER_EXIST_CODE = 'PHONE_NUMBER_EXIST';
const ERROR_MESSAGE_ID_BY_CODE: Record<string, string> = {
  [PHONE_NUMBER_EXIST_CODE]: 'login.phoneVerification.errors.alreadyUsed',
  INVALID_OTP: 'login.twoFactor.invalidCode',
  OTP_EXPIRED: 'login.twoFactor.invalidCode',
  OTP_NOT_FOUND: 'login.twoFactor.invalidCode',
  TOO_MANY_ATTEMPTS: 'login.phoneVerification.errors.tooManyAttempts',
  TOO_MANY_REQUESTS: 'login.phoneVerification.errors.tooManyRequests',
  OTP_RESEND_TOO_SOON: 'login.phoneVerification.errors.resendTooSoon',
  INVALID_PHONE_NUMBER: 'login.phoneVerification.errors.invalidPhone',
  [IMPERSONATION_BLOCKED_CODE]: IMPERSONATION_BLOCKED_MESSAGE_ID,
};

export const PHONE_VERIFICATION_REQUIRED_CODE = 'PHONE_VERIFICATION_REQUIRED';
export const PHONE_BANNER_DISMISS_KEY_PREFIX = 'phone-verification-banner-dismissed:';
const BANNER_DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
export const OTP_RESEND_COOLDOWN_MS = 60 * 1000;
const OTP_COOLDOWN_STORAGE_KEY = 'phone-otp-cooldown-ends-at';

export const getPhoneVerificationErrorMessageId = (code: string | undefined): string =>
  isNil(code) ? GENERIC_ERROR_ID : (ERROR_MESSAGE_ID_BY_CODE[code] ?? GENERIC_ERROR_ID);

export const isWorkspaceAdmin = ({
  rootUser,
  userRights,
}: {
  rootUser: boolean;
  userRights: UserRight[] | null;
}): boolean => rootUser || isSettingsOrganizationOn(userRights ?? []);

export const isPhoneBannerDismissed = (userId: string, now: number = Date.now()): boolean => {
  const dismissedAtRaw = window.localStorage.getItem(`${PHONE_BANNER_DISMISS_KEY_PREFIX}${userId}`);
  if (isNil(dismissedAtRaw)) {
    return false;
  }
  const dismissedAt = Number(dismissedAtRaw);
  return Number.isFinite(dismissedAt) && now - dismissedAt < BANNER_DISMISS_DURATION_MS;
};

export const dismissPhoneBanner = (userId: string, now: number = Date.now()): void => {
  window.localStorage.setItem(`${PHONE_BANNER_DISMISS_KEY_PREFIX}${userId}`, String(now));
};

export const getOtpCooldownEndsAt = (): number | null => {
  const raw = window.sessionStorage.getItem(OTP_COOLDOWN_STORAGE_KEY);
  if (isNil(raw)) {
    return null;
  }
  const endsAt = Number(raw);
  return Number.isFinite(endsAt) ? endsAt : null;
};

export const startOtpCooldown = (now: number = Date.now()): number => {
  const endsAt = now + OTP_RESEND_COOLDOWN_MS;
  window.sessionStorage.setItem(OTP_COOLDOWN_STORAGE_KEY, String(endsAt));
  return endsAt;
};

export const getOtpCooldownSecondsLeft = (endsAt: number | null, now: number = Date.now()): number =>
  isNil(endsAt) ? 0 : Math.max(0, Math.ceil((endsAt - now) / 1000));
